import { Link } from '@tanstack/react-router';
import { Ban, ChevronRight, Pause, Play, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { ChannelChip, DateChip, SourceDestinationChip } from '~/components/common/chips';
import { Button } from '~/components/ui/button';
import { StatusChip } from '~/features/catalog-operations/components/chips';
import type {
  BatchHeader,
  BatchStatus,
} from '~/features/catalog-operations/types/catalog-operations.types';
import {
  batchFlowIcons,
  resolveBatchChipStatus,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import { resolveBatchStatusDisplay } from '~/features/catalog-operations/utils/batch-status-display';
import { lastActionLabelKey } from '~/features/catalog-operations/utils/last-action';
import { CostApprovalWaitingNote } from '~/features/cost-estimate/components/cost-approval-waiting';
import type { CostWaiting } from '~/features/cost-estimate/utils/cost-waiting';
import { ArchivedBadge } from '~/features/list-archive/components/archived-badge';
import { formatAuditTimestamp } from '~/utils/date';

import type { BatchRuns } from '../../types/batch-retry.api.types';
import type { BatchRetryState } from '../../utils/batch-retry-state';

import { BatchRunChips } from './batch-retry-panel';

// When opened from an experiment, `detail.status` was folded from the
// experiment's own RUNNING/COMPLETED wire values (see `toBatchStatus`) —
// relabel the chip (and, for "Running", recolor it) to match that vocabulary
// and the `ExperimentStatusChip` token classes instead of the catalog-ops
// In Progress/Delivered treatment, which doesn't apply to an experiment.
// "Completed" keeps the default `delivered` green — it already matches
// `ExperimentStatusChip`'s `bg-success-50 text-success` for COMPLETED.
const EXPERIMENT_STATUS_OVERRIDES: Partial<
  Record<BatchStatus, { label: string; className?: string }>
> = {
  'in-progress': {
    label: 'Running',
    className: 'bg-exp-status-running-bg text-exp-status-running',
  },
  delivered: { label: 'Completed' },
};

/** Batch pause / retry: header actions + run chips, driven by the server's `availableActions`. */
export interface BatchRetryHeaderProps {
  runs: BatchRuns;
  state: BatchRetryState;
  onPause: () => void;
  onResume: () => void;
  /** A pause / resume request is in flight. */
  pauseResumePending: boolean;
  onExclude: () => void;
  /** Chip click → Runs tab; a retry chip also opens that run's drawer. */
  onSelectRun: (runId: string | null) => void;
}

interface Props {
  /** The header slice powers this component; a full `BatchDetail` is assignable
   *  since it extends `BatchHeader`. */
  detail: BatchHeader;
  /** Set when this batch was opened from an experiment's "View Batch
   *  Details" CTA — see `EXPERIMENT_STATUS_OVERRIDES` above. */
  source?: 'experiment';
  /** Optional top-right action slot (e.g. the "Open enrichment workflow"
   *  icon button), rendered on the breadcrumb row aligned to the far right. */
  action?: ReactNode;
  /** Run paused at the cost gate (header `costGate`, or the page's estimate) — the chip
   *  reads "Awaiting cost approval" and the provenance row links to the estimate. */
  costWaiting?: CostWaiting | null;
  /** Pause / resume / retry / exclude actions and the run chips; absent = legacy header. */
  retry?: BatchRetryHeaderProps;
}

export function BatchDetailHeader({ detail, source, action, costWaiting = null, retry }: Props) {
  // Shared actor labels ("Created by" / "Last action by") live in the common ns.
  const { t: tc } = useTranslation('common');
  const lastActionKey = lastActionLabelKey(detail.lastActionType);
  // Trial batches don't get a real delivery step — once they're paused at
  // AWAITING_APPROVAL there's nothing left for the trial user to wait on, so
  // the status chip reads as "Completed" (delivered's green treatment) rather
  // than the generic in-progress state.
  const isTrialCompleted = detail.isTrial && detail.isAwaitingApproval;
  const experimentOverride =
    !isTrialCompleted && source === 'experiment'
      ? EXPERIMENT_STATUS_OVERRIDES[detail.status]
      : undefined;
  const statusLabel = isTrialCompleted ? 'Completed' : experimentOverride?.label;
  // Server-reconciled batch status (root + retries) wins over the legacy chip where present.
  const batchDisplay =
    !isTrialCompleted && !experimentOverride && source !== 'experiment'
      ? resolveBatchStatusDisplay({
          batchStatus: retry?.runs.batchStatus ?? detail.batchStatus,
          fallbackStatus: detail.status,
          retryAttempts: retry?.runs.attemptsUsed ?? detail.retryAttempts,
        })
      : null;
  return (
    <div data-testid="batch-detail-header" className="space-y-4">
      <nav data-testid="batch-detail-breadcrumb" className="flex items-center gap-1.5 text-sm">
        <Link
          to="/catalog-operations"
          data-testid="batch-detail-breadcrumb-link"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
        >
          <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
          <span>Catalog Operations</span>
        </Link>
        <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        <span className="font-semibold">Batch Details</span>
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 data-testid="batch-detail-title" className="text-2xl font-bold tracking-tight">
            {detail.name.trim() !== '' ? detail.name : detail.batchId}
          </h1>
          <StatusChip
            status={
              batchDisplay
                ? resolveBatchChipStatus(batchDisplay.status, detail.triggerSource)
                : isTrialCompleted
                  ? 'delivered'
                  : resolveBatchChipStatus(detail.status, detail.triggerSource)
            }
            {...(batchDisplay
              ? { label: batchDisplay.label }
              : statusLabel
                ? { label: statusLabel }
                : {})}
            {...(batchDisplay?.className
              ? { className: batchDisplay.className }
              : experimentOverride?.className
                ? { className: experimentOverride.className }
                : {})}
            costWaiting={costWaiting}
          />
          {/* List Archive: says why a deep-linked batch is missing from the default list. */}
          <ArchivedBadge archivedAt={detail.archivedAt} archivedBy={detail.archivedBy} />
          <ChannelChip
            platform={detail.sourcePlatform}
            label={detail.source}
            darkBg={true}
            testId="batch-detail-channel-chip"
          />
        </div>

        <div
          data-testid="batch-detail-header-actions"
          className="flex flex-wrap items-center gap-2"
        >
          {retry && <BatchRetryActions {...retry} />}
          {action}
          <DateChip iso={detail.createdAt} testId="batch-detail-created-at-chip" />
          {detail.deliveredAt && (
            <>
              <span className="text-muted-foreground">•</span>
              <DateChip iso={detail.deliveredAt} testId="batch-detail-delivered-at-chip" />
            </>
          )}
          <span className="text-muted-foreground">•</span>
          <SourceDestinationChip
            sourcePlatform={detail.sourcePlatform}
            destinationPlatform={detail.destinationPlatform}
            {...batchFlowIcons(detail.triggerSource)}
            darkBg={true}
            testId="batch-detail-platforms-chip"
          />
        </div>
      </div>

      {retry && retry.runs.runs.length > 0 && (
        <BatchRunChips runs={retry.runs} onSelectRun={retry.onSelectRun} />
      )}

      {/* Provenance: who created the batch and who last acted on it, from the
          workflow-service audit stamps. Last-action is shown only when present. */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {costWaiting ? <CostApprovalWaitingNote waiting={costWaiting} /> : null}
        <span data-testid="batch-detail-created-by" className="flex min-w-0 items-center gap-1.5">
          {tc('actor.createdBy')}
          <ActorStringLabel
            value={detail.createdBy}
            showAvatar={false}
            className="text-xs"
            testId="batch-detail-created-by-actor"
          />
          {detail.createdAt && (
            <>
              <span aria-hidden>·</span>
              <span className="tabular-nums">{formatAuditTimestamp(detail.createdAt)}</span>
            </>
          )}
        </span>
        {(Boolean(detail.lastActionBy) || Boolean(detail.lastActionAt)) && (
          <span
            data-testid="batch-detail-last-action-by"
            className="flex min-w-0 items-center gap-1.5"
          >
            {tc('actor.lastActionBy')}
            {/* "Approved · ana@…": the action's label, when it has one, ahead of the actor. */}
            {lastActionKey && (
              <span data-testid="batch-detail-last-action-type" className="text-foreground">
                {tc(lastActionKey)}
                {detail.lastActionBy ? ' ·' : ''}
              </span>
            )}
            <ActorStringLabel
              value={detail.lastActionBy}
              showAvatar={false}
              className="text-xs"
              testId="batch-detail-last-action-by-actor"
            />
            {detail.lastActionAt && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{formatAuditTimestamp(detail.lastActionAt)}</span>
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Pause / Resume / Exclude — only what the server lists. Retry and the products still not enriched
 * live in the Runs panel (Run Details tab), next to the runs they come from.
 */
function BatchRetryActions({
  state,
  onPause,
  onResume,
  pauseResumePending,
  onExclude,
}: BatchRetryHeaderProps) {
  const has = state.has;
  const left = state.left.toLocaleString();
  return (
    <div data-testid="batch-retry-actions" className="flex flex-wrap items-center gap-2">
      {has('PAUSE') && (
        <Button
          data-testid="batch-action-pause"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          isLoading={pauseResumePending}
          onClick={onPause}
        >
          <Pause aria-hidden="true" /> Pause
        </Button>
      )}
      {has('RESUME') && (
        <Button
          data-testid="batch-action-resume"
          size="sm"
          className="cursor-pointer"
          isLoading={pauseResumePending}
          onClick={onResume}
        >
          <Play aria-hidden="true" /> Resume
        </Button>
      )}
      {/* Exclude is the way out once retries are used up; while a retry can still help, it waits. */}
      {has('EXCLUDE_REMAINING') && !has('RETRY_REMAINING') && (
        <Button
          data-testid="batch-action-exclude"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          onClick={onExclude}
        >
          <Ban aria-hidden="true" /> Exclude {left}
        </Button>
      )}
    </div>
  );
}
